import CloseIcon from "@mui/icons-material/Close"
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft"
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight"
import ZoomInIcon from "@mui/icons-material/ZoomIn"
import ZoomOutIcon from "@mui/icons-material/ZoomOut"
import { Box, Chip, IconButton, Modal, SxProps, Typography } from "@mui/material"
import Image from "next/image"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { TImagePrev } from "../../../interface/ImagePreview"
import Loader from "../Loader/Loader"

const style: SxProps = {
  position: "absolute" as "absolute",
  width: "95vw",
  height: "95vh",
  overflow: "hidden",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  "&:focus-visible": {
    outline: "none",
  },
}

const ActionStyles: SxProps = {
  "& .Preview_Counter": {
    position: "absolute",
    bottom: 10,
    left: "50%",
    transform: "translateX(-50%)",
    background: "rgba(0, 0, 0, 0.5)",
  },
  "& .Preview_Prev": {
    position: "absolute",
    left: 10,
    width: "3rem",
    height: "3rem",
    borderRadius: "50%",
    cursor: "pointer",
    background: "rgba(0, 0, 0, 0.5)",
  },
  "& .Preview_Next": {
    position: "absolute",
    right: 10,
    width: "3rem",
    height: "3rem",
    borderRadius: "50%",
    cursor: "pointer",
    background: "rgba(0, 0, 0, 0.7)",
  },
}

const ZoomGroup: SxProps = {
  position: "absolute",
  right: 15,
  bottom: 10,
  background: "rgba(0, 0, 0, 0.5)", //
  borderRadius: "10px",
  "& button": {
    padding: "0 12px",
    height: "32px",
    borderRadius: 0,
  },
  "& button:first-of-type": {
    borderRadius: "10px 0 0 10px",
  },
  "& button:last-child": {
    borderRadius: "0 10px 10px 0",
  },
  "& .ResetZoom": {
    padding: "0 5px",
    fontSize: "12px",
  },
}

const HeaderRightGroup: SxProps = {
  position: "absolute",
  top: 10,
  right: 10,
}

type ImageType = string | { url: string; title?: string }

const extractImageUrl = (image: ImageType): string => {
  return typeof image === "string" ? image : image.url
}

const extractImageTitle = (image: ImageType): string | undefined => {
  return typeof image === "string" ? undefined : image.title
}


const ImagePreview = ({ startIndex = 0, images = [], isOpen, onClose }: TImagePrev) => {
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState<boolean>(true)
  const [index, setIndex] = useState<number>(startIndex)
  const [zoomLevel, setZoomLevel] = useState<number>(100)
  const [isGrabbing, setIsGrabbing] = useState(false)
  const imageRef = useRef<HTMLImageElement>(null)

  const onClickPrev = useCallback(() => {
    setZoomLevel(100)
    if (index >= 1) {
      // console.info("Event Left", index)
      setLoading(true)
      setIndex((i) => i - 1)
    }
  }, [index])

  const onClickNext = useCallback(() => {
    setZoomLevel(100)
    if (index < images.length - 1) {
      // console.info("Event Right", index)
      setLoading(true)
      setIndex((i) => i + 1)
    }
  }, [images.length, index])

  const handleZoomIn = useCallback(() => {
    if (zoomLevel <= 1000) {
      setZoomLevel((zL) => zL + 100)
      if (imageRef.current) {
        const { width, height } = imageRef.current
        imageRef.current.style.width = `${width + 100}px`
        imageRef.current.style.height = `${height + 100}px`
      }
    }
  }, [zoomLevel])

  const handleZoomOut = useCallback(() => {
    if (zoomLevel > 100) {
      setZoomLevel((zL) => zL - 100)
      if (imageRef.current) {
        const { width, height } = imageRef.current
        imageRef.current.style.width = `${width - 100}px`
        imageRef.current.style.height = `${height - 100}px`
      }
    }
  }, [zoomLevel])


  const handleArrowEvents = useCallback((event: KeyboardEvent) => {
    const key = event.key // "ArrowRight", "ArrowLeft", "ArrowUp", or "ArrowDown" // Future Use if WASD needed
    const callback = {
      ArrowLeft: onClickPrev,
      ArrowRight: onClickNext,
      ArrowUp: handleZoomIn,
      ArrowDown: handleZoomOut,
    }[key]

    callback?.()
  }, [handleZoomIn, handleZoomOut, onClickNext, onClickPrev])


  useEffect(() => {
    document.addEventListener("keydown", handleArrowEvents)
    return () => {
      document.removeEventListener("keydown", handleArrowEvents)
    }
  }, [handleArrowEvents])

  useEffect(() => {
    setOpen(isOpen)
    if (isOpen) setLoading(true)
  }, [isOpen])

  useEffect(() => {
    // console.info("Start Index :", startIndex)
    setIndex(startIndex)
  }, [startIndex, images])

  useEffect(() => {
    if (zoomLevel === 100) {
      if (imageRef.current) {
        imageRef.current.style.width = `100%`
        imageRef.current.style.height = `100%`
      }
    }
  }, [zoomLevel])

  const handleZoomReset = () => {
    if (zoomLevel > 100) {
      setZoomLevel(() => 100)
      if (imageRef.current) {
        const { width, height } = imageRef.current
        imageRef.current.style.width = `${width - 100}px`
        imageRef.current.style.height = `${height - 100}px`
      }
    }
  }

  const startGrabbing = (e: React.MouseEvent<HTMLDivElement>) => {
    if (zoomLevel !== 100) {
      setIsGrabbing(true)
      e.preventDefault()
    }
  }

  // Function to stop grabbing
  const stopGrabbing = () => {
    setIsGrabbing(false)
  }

  // Function to handle grabbing and scrolling
  const handleGrabScroll = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isGrabbing) {
      const container = document.getElementById("image-container")
      if (container) {
        container.scrollLeft -= e.movementX
        container.scrollTop -= e.movementY
      }
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="Preview Modal"
      aria-describedby="A Modal to Preview Group of Images"
      sx={{
        justifyContent: "center",
        display: "flex",
        alignItems: "center",
        backgroundColor: "transparent",
      }}
    >
      <>
        <Box sx={style}>
          {Array.isArray(images) && (
            <>
              <Box
                id="image-container"
                sx={{
                  position: "relative",
                  height: "100%",
                  width: "100%",
                  userSelect: "none",
                  overflow: zoomLevel <= 100 ? "hidden" : "auto",
                  cursor: isGrabbing ? "grabbing" : "auto",
                }}
                onMouseDown={startGrabbing}
                onMouseUp={stopGrabbing}
                onMouseLeave={stopGrabbing}
                onMouseMove={handleGrabScroll}
              >
                <Loader type="InfiLoader" loading={loading} />
                {images[index] && (
                  <Box sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    filter: loading ? "blur(10px)" : "blur(0)",
                    cursor: zoomLevel === 100 ? "auto" : "grab",
                    userSelect: "none",
                    pointerEvents: "none",
                    transition: "0.5s",
                  }}>
                    <Image
                      ref={imageRef}
                      src={extractImageUrl(images[index])}
                      height={0}
                      width={0}
                      sizes="90vw"
                      alt="preview"
                      onLoadingComplete={(e) => {
                        setLoading(false)
                      }}
                      style={{
                        position: 'relative',

                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                        filter: loading ? "blur(10px)" : "blur(0)",
                        cursor: zoomLevel === 100 ? "auto" : "grab",
                        userSelect: "none",
                        pointerEvents: "none",
                        transition: "0.5s",
                      }}
                    />
                    {extractImageTitle(images[index])?.length ? <Typography position='absolute' sx={{bottom: 48, right: 20, backgroundColor: '#212121', paddingY: '4px', paddingX: '8px', borderRadius: '8px'}}>{extractImageTitle(images[index])}</Typography> : null}
                  </Box>
                )}
              </Box>
            </>
          )}
          <Box sx={ActionStyles}>
            {index >= 1 && <KeyboardArrowLeftIcon className={"Preview_Prev"} onClick={onClickPrev} />}
            <Chip className={"Preview_Counter"} label={`${index + 1} / ${images.length}`} />
            {index < images.length - 1 && <KeyboardArrowRightIcon className={"Preview_Next"} onClick={onClickNext} />}
            <Box className={"ZoomGroup"} sx={ZoomGroup}>
              <IconButton onClick={handleZoomIn} disabled={zoomLevel >= 1000}>
                <ZoomInIcon />
              </IconButton>
              <IconButton className={"ResetZoom"} onClick={handleZoomReset} disabled={zoomLevel <= 100}>
                {`${zoomLevel}%`}
              </IconButton>
              <IconButton onClick={handleZoomOut} disabled={zoomLevel <= 100}>
                <ZoomOutIcon />
              </IconButton>
            </Box>
            <Box className={"HeaderRightGroup"} sx={HeaderRightGroup}>
              <IconButton onClick={onClose}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </Box>
      </>
    </Modal>
  )
}

export default ImagePreview
